import { FC, Fragment, useMemo } from "react";
import styled from "@emotion/styled";
import proactiveTrialMacosImageUrl from "@/assets/images/onboarding/proactive-trial-macos.png";
import proactiveTrialWindowsImageUrl from "@/assets/images/onboarding/proactive-trial-windows.png";
import { MemAgentShortcutDropdown } from "@/components/mem-agent/mem-agent-shortcut-dropdown";
import type { RemindersDeliveryOption } from "@/domains/metrics/tracked-event";
import {
  MDSActionMenuDropdown,
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconColor,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconKind,
  MDSLink,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuDropdownSlotName,
  MDSRadio,
  MDSRadioGroup,
  MDSToggle,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  SettingsIntegrationHeader,
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import { SettingsEmailSenderAllowlistSection } from "@/pages/settings/components/settings-email-sender-allowlist-section";
import { clientEnvModule } from "@/modules/client-env";
import type {
  MemAgentEmailAllowlistEntry,
  UpdateMemAgentEmailSenderAllowlistArgs,
} from "@/store/mem-agent/mem-agent-store";
import {
  getDefaultMemAgentHoldToTalkKey,
  type MemAgentHoldToTalkKey,
  type MemAgentHoldToTalkKeySetting,
} from "@/modules/mem-agent/hold-to-talk-key";
import type { FloatingAgentPresence } from "@/store/floating-agent/AppStoreFloatingAgentStore";

export type SettingsMemAgentPageKind = "general" | "communication-channels";

export interface SettingsMemAgentPageViewProps {
  pageKind?: SettingsMemAgentPageKind;
  isConnectingSlack: boolean;
  isSlackOAuthPending?: boolean;
  isRemovingSlack: boolean;
  isSlackConnected: boolean;
  isWhatsAppConnected: boolean;
  whatsAppPhoneNumber: string | null;
  isConnectingWhatsApp: boolean;
  isRemovingWhatsApp: boolean;
  isIMessageConnected: boolean;
  imessagePhoneNumber: string | null;
  isConnectingIMessage: boolean;
  isRemovingIMessage: boolean;
  isWindows?: boolean;
  subscriptionNoticeLabel?: string;
  subscriptionNoticeActionLabel?: string;
  hasActiveBillingSubscription?: boolean;
  hasGrantedSubscriptionAccess?: boolean;
  onEnableOption: (option: RemindersDeliveryOption) => void;
  onOpenSlack: () => void;
  onOpenLearnMore: () => void;
  onOpenProactiveUpsell?: () => void;
  onReauthorizeSlack: () => void;
  onRemoveSlack: () => void;
  onConnectWhatsApp: () => void;
  onRemoveWhatsApp: () => void;
  onConnectIMessage: () => void;
  onRemoveIMessage: () => void;
  onOpenSubscriptionManagement?: () => void;
  emailChannelAddress?: string;
  protectedEmailSenderAllowlistEntry?: MemAgentEmailAllowlistEntry;
  emailSenderAllowlist: MemAgentEmailAllowlistEntry[];
  holdToTalkKey: MemAgentHoldToTalkKeySetting;
  holdToTalkIsEnabled: boolean;
  newMessageSoundEnabled: boolean;
  floatingAgentPresence: FloatingAgentPresence;
  isHoldToTalkSettingDisabled?: boolean;
  isSavingHoldToTalkKey?: boolean;
  isSavingEmailSenderAllowlist: boolean;
  showFloatingAgentSettings?: boolean;
  showEmailSenderAllowlist?: boolean;
  canSaveEmailSenderAllowlist?: boolean;
  onSaveEmailSenderAllowlist: (
    args: UpdateMemAgentEmailSenderAllowlistArgs
  ) => Promise<boolean | void> | boolean | void;
  onHoldToTalkKeyChange: (holdToTalkKey: MemAgentHoldToTalkKey) => void;
  onHoldToTalkEnabledChange: (enabled: boolean) => void;
  onNewMessageSoundEnabledChange: (enabled: boolean) => void;
  onFloatingAgentPresenceChange: (presence: FloatingAgentPresence) => void;
}

interface ConnectorConfig {
  option: RemindersDeliveryOption;
  title: string;
  buttonLabel: string;
  iconKind: MDSIconKind;
}

interface ConnectorRow {
  option: RemindersDeliveryOption;
  isConnected: boolean;
  element: JSX.Element;
}

const connectorConfigs: ConnectorConfig[] = [
  {
    option: "iMessage",
    title: "iMessage",
    buttonLabel: "Enable",
    iconKind: MDSIconKind.Comment,
  },
  {
    option: "WhatsApp",
    title: "WhatsApp",
    buttonLabel: "Enable",
    iconKind: MDSIconKind.WhatsApp,
  },
  {
    option: "Slack",
    title: "Slack",
    buttonLabel: "Enable",
    iconKind: MDSIconKind.Slack,
  },
  {
    option: "Teams",
    title: "Teams",
    buttonLabel: "Join waitlist",
    iconKind: MDSIconKind.Teams,
  },
];

/**
 * Determines whether a communication connector belongs in the connected section.
 */
const isConnectedConnector = ({
  option,
  isSlackConnected,
  isWhatsAppConnected,
  isIMessageConnected,
}: {
  option: RemindersDeliveryOption;
  isSlackConnected: boolean;
  isWhatsAppConnected: boolean;
  isIMessageConnected: boolean;
}): boolean => {
  if (option === "Slack") {
    return isSlackConnected;
  }

  if (option === "WhatsApp") {
    return isWhatsAppConnected;
  }

  if (option === "iMessage") {
    return isIMessageConnected;
  }

  return false;
};

/**
 * Renders the Mem Agent reminders settings surface and connector list.
 */
export const SettingsMemAgentPageView: FC<SettingsMemAgentPageViewProps> = ({
  pageKind = "general",
  isConnectingSlack,
  isSlackOAuthPending = false,
  isRemovingSlack,
  isSlackConnected,
  isWhatsAppConnected,
  whatsAppPhoneNumber,
  isConnectingWhatsApp,
  isRemovingWhatsApp,
  isIMessageConnected,
  imessagePhoneNumber,
  isConnectingIMessage,
  isRemovingIMessage,
  isWindows,
  subscriptionNoticeLabel,
  subscriptionNoticeActionLabel = "Upgrade to maintain access",
  hasActiveBillingSubscription = false,
  hasGrantedSubscriptionAccess = false,
  onEnableOption,
  onOpenSlack,
  onOpenLearnMore,
  onOpenProactiveUpsell,
  onReauthorizeSlack,
  onRemoveSlack,
  onConnectWhatsApp,
  onRemoveWhatsApp,
  onConnectIMessage,
  onRemoveIMessage,
  onOpenSubscriptionManagement,
  emailChannelAddress,
  protectedEmailSenderAllowlistEntry,
  emailSenderAllowlist,
  holdToTalkKey,
  holdToTalkIsEnabled,
  newMessageSoundEnabled,
  floatingAgentPresence,
  isHoldToTalkSettingDisabled = false,
  isSavingHoldToTalkKey = false,
  isSavingEmailSenderAllowlist,
  showFloatingAgentSettings = false,
  showEmailSenderAllowlist = false,
  canSaveEmailSenderAllowlist = true,
  onSaveEmailSenderAllowlist,
  onHoldToTalkKeyChange,
  onHoldToTalkEnabledChange,
  onNewMessageSoundEnabledChange,
  onFloatingAgentPresenceChange,
}) => {
  const slackAccountMenu = useMemo<MDSMenuContentList>(
    () => [
      {
        id: "reauthorize-slack",
        kind: MDSMenuContentItemKind.Item,
        label: "Reauthorize Slack",
        icon: MDSIconKind.Refresh,
        onClick: onReauthorizeSlack,
        isDisabled: isConnectingSlack || isSlackOAuthPending || isRemovingSlack,
      },
      {
        id: "remove-account",
        kind: MDSMenuContentItemKind.Item,
        label: "Remove account",
        icon: MDSIconKind.Ban,
        onClick: onRemoveSlack,
        isDisabled: isConnectingSlack || isSlackOAuthPending || isRemovingSlack,
      },
    ],
    [isConnectingSlack, isRemovingSlack, isSlackOAuthPending, onReauthorizeSlack, onRemoveSlack]
  );
  const whatsAppAccountMenu = useMemo<MDSMenuContentList>(
    () => [
      {
        id: "remove-account",
        kind: MDSMenuContentItemKind.Item,
        label: "Remove account",
        icon: MDSIconKind.Ban,
        onClick: onRemoveWhatsApp,
        isDisabled: isRemovingWhatsApp,
      },
    ],
    [isRemovingWhatsApp, onRemoveWhatsApp]
  );
  const imessageAccountMenu = useMemo<MDSMenuContentList>(
    () => [
      {
        id: "remove-account",
        kind: MDSMenuContentItemKind.Item,
        label: "Remove account",
        icon: MDSIconKind.Ban,
        onClick: onRemoveIMessage,
        isDisabled: isRemovingIMessage,
      },
    ],
    [isRemovingIMessage, onRemoveIMessage]
  );
  const handleFloatingAgentPresenceChange = (presence: string) => {
    if (presence !== "always" && presence !== "unread" && presence !== "on_demand") return;

    onFloatingAgentPresenceChange(presence);
  };
  const shouldShowWindowsIntroImage = isWindows ?? clientEnvModule.isWindows();
  const introImageUrl = shouldShowWindowsIntroImage
    ? proactiveTrialWindowsImageUrl
    : proactiveTrialMacosImageUrl;
  const shouldUseSubscriptionManagementAction =
    hasActiveBillingSubscription || hasGrantedSubscriptionAccess;
  const introSubscriptionActionLabel = shouldUseSubscriptionManagementAction
    ? "Manage subscription"
    : "Start 7-day free trial";
  const handleIntroSubscriptionAction = shouldUseSubscriptionManagementAction
    ? onOpenSubscriptionManagement
    : (onOpenProactiveUpsell ?? onOpenSubscriptionManagement);
  const isHoldToTalkShortcutEnabled = holdToTalkIsEnabled;
  const isHoldToTalkControlDisabled = isHoldToTalkSettingDisabled || isSavingHoldToTalkKey;
  const resolvedHoldToTalkKey = holdToTalkKey ?? getDefaultMemAgentHoldToTalkKey({ isWindows });

  const subscriptionNotice = subscriptionNoticeLabel ? (
    <StyledSubscriptionNotice>
      <StyledSubscriptionNoticeTitle>
        <MDSText
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.Smedium}
          color={MDSTextColor.Contrast}
        >
          {subscriptionNoticeLabel}
        </MDSText>
        <MDSIcon kind={MDSIconKind.Warning} size={14} boxSize={16} />
      </StyledSubscriptionNoticeTitle>
      <StyledSubscriptionNoticeLink
        variant={MDSTextVariant.TextXs}
        onPress={onOpenSubscriptionManagement}
      >
        {subscriptionNoticeActionLabel}
      </StyledSubscriptionNoticeLink>
    </StyledSubscriptionNotice>
  ) : undefined;

  const channelRows: ConnectorRow[] = connectorConfigs.map(connector => {
    const isSlackOption = connector.option === "Slack";
    const isConnectedSlackOption = isSlackOption && isSlackConnected;
    const isWhatsAppOption = connector.option === "WhatsApp";
    const isIMessageOption = connector.option === "iMessage";
    const isAvailableAgentOption = isWhatsAppOption || isIMessageOption;
    const isConnectedConnectorOption = isConnectedConnector({
      option: connector.option,
      isSlackConnected,
      isWhatsAppConnected,
      isIMessageConnected,
    });

    const isConnectedAgentOption = (() => {
      if (isWhatsAppOption) {
        return isWhatsAppConnected;
      }

      if (isIMessageOption) {
        return isIMessageConnected;
      }

      return false;
    })();

    const agentPhoneNumber = (() => {
      if (isWhatsAppOption) {
        return whatsAppPhoneNumber;
      }

      if (isIMessageOption) {
        return imessagePhoneNumber;
      }

      return null;
    })();

    const disconnectedAgentHint = isWhatsAppOption
      ? "Open WhatsApp and send the prefilled message to connect."
      : "Open Messages and send the prepared message to connect.";
    const agentAccountMenu = isWhatsAppOption ? whatsAppAccountMenu : imessageAccountMenu;
    const connectorButtonAction = (() => {
      if (isWhatsAppOption) {
        return onConnectWhatsApp;
      }

      if (isIMessageOption) {
        return onConnectIMessage;
      }

      if (isConnectedSlackOption) {
        return onOpenSlack;
      }

      return () => onEnableOption(connector.option);
    })();

    const connectorButtonLabel = (() => {
      if (isConnectedSlackOption) {
        return "Go to Slack";
      }

      return connector.buttonLabel;
    })();

    const isConnectorButtonLoading = (() => {
      if (isWhatsAppOption) {
        return isConnectingWhatsApp;
      }

      if (isIMessageOption) {
        return isConnectingIMessage;
      }

      return isSlackOption && !isSlackConnected && isConnectingSlack;
    })();

    const element = (
      <StyledConnectorRow aria-label={connector.title}>
        <StyledConnectorIcon aria-hidden="true">
          <MDSIcon kind={connector.iconKind} size={16} boxSize={16} />
        </StyledConnectorIcon>
        <StyledConnectorCopy>
          <StyledConnectorTitle variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
            {connector.title}
          </StyledConnectorTitle>
          {isAvailableAgentOption ? (
            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              {isConnectedAgentOption && agentPhoneNumber
                ? `Connected as ${agentPhoneNumber}`
                : disconnectedAgentHint}
            </MDSText>
          ) : null}
        </StyledConnectorCopy>
        <StyledConnectorActions>
          {isAvailableAgentOption && isConnectedAgentOption ? (
            <StyledConnectedStatus>
              <MDSIcon
                kind={MDSIconKind.CheckCircleSolid}
                color={MDSIconColor.Positive}
                size={16}
                boxSize={16}
              />
              <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                Connected
              </MDSText>
            </StyledConnectedStatus>
          ) : (
            <StyledConnectorButton
              onPress={connectorButtonAction}
              isLoading={isConnectorButtonLoading}
            >
              {connectorButtonLabel}
              {isConnectedSlackOption ? (
                <MDSIcon kind={MDSIconKind.ExternalLink} slot={MDSButtonSlotName.RightIcon} />
              ) : null}
            </StyledConnectorButton>
          )}
          {isConnectedSlackOption ? (
            <MDSActionMenuDropdown contentList={slackAccountMenu} placement="bottom right">
              <MDSIconButton
                slot={MDSMenuDropdownSlotName.Trigger}
                aria-label="Slack account actions"
                iconKind={MDSIconKind.More}
                size={MDSIconButtonSize.Medium}
              />
            </MDSActionMenuDropdown>
          ) : null}
          {isAvailableAgentOption && isConnectedAgentOption ? (
            <MDSActionMenuDropdown contentList={agentAccountMenu} placement="bottom right">
              <MDSIconButton
                slot={MDSMenuDropdownSlotName.Trigger}
                aria-label={`${connector.option} account actions`}
                iconKind={MDSIconKind.More}
                size={MDSIconButtonSize.Medium}
              />
            </MDSActionMenuDropdown>
          ) : null}
        </StyledConnectorActions>
      </StyledConnectorRow>
    );

    return {
      option: connector.option,
      isConnected: isConnectedConnectorOption,
      element,
    };
  });
  const connectedChannelRows = channelRows.filter(row => row.isConnected);
  const availableChannelRows = channelRows.filter(row => !row.isConnected);
  const showConnectedChannels = showEmailSenderAllowlist || connectedChannelRows.length > 0;

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader
        breadcrumb="Mem Agent"
        title={pageKind === "general" ? "Mem Agent Settings" : "Communication Channels"}
        rightContent={pageKind === "general" ? subscriptionNotice : undefined}
      />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          {pageKind === "general" ? (
            <StyledContentStack $pageKind="general">
              <StyledIntroPanel>
                <StyledIntroCopy>
                  <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.Smedium}>
                    Mem Agent is better with Proactive
                  </MDSText>
                  <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
                    On Free, Mem Agent answers when you ask. With Proactive, it tracks follow-ups,
                    deadlines, and loose ends from your notes and meetings — plus anything you tell
                    it to remember — and reminds you at the right time.
                  </MDSText>
                  <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
                    Free includes 25 notes and 25 Mem Agent messages per month.
                  </MDSText>
                  <StyledIntroActions>
                    <MDSButton variant={MDSButtonVariant.Default} onPress={onOpenLearnMore}>
                      Learn more
                      <MDSIcon
                        kind={MDSIconKind.ArrowUpRightFromSquare}
                        slot={MDSButtonSlotName.RightIcon}
                      />
                    </MDSButton>
                    <MDSButton
                      variant={MDSButtonVariant.Emphasis}
                      onPress={handleIntroSubscriptionAction}
                    >
                      {shouldUseSubscriptionManagementAction ? null : (
                        <MDSIcon kind={MDSIconKind.StarSolid} slot={MDSButtonSlotName.LeftIcon} />
                      )}
                      {introSubscriptionActionLabel}
                    </MDSButton>
                  </StyledIntroActions>
                </StyledIntroCopy>
                <StyledIntroMediaFrame $isWindows={shouldShowWindowsIntroImage}>
                  <StyledIntroMediaImage src={introImageUrl} alt="Mem Agent response preview" />
                </StyledIntroMediaFrame>
              </StyledIntroPanel>

              {showFloatingAgentSettings ? (
                <>
                  <StyledFloatingAgentSection aria-label="Floating Mem Agent Presence">
                    <StyledSectionHeader
                      variant={MDSTextVariant.TextXs}
                      weight={MDSTextWeight.Smedium}
                      color={MDSTextColor.Secondary}
                    >
                      FLOATING MEM AGENT PRESENCE
                    </StyledSectionHeader>
                    <StyledFloatingAgentSettingsCard>
                      <StyledPresenceRadioGroup
                        aria-label="Floating Mem Agent presence"
                        value={floatingAgentPresence}
                        onChange={handleFloatingAgentPresenceChange}
                      >
                        <StyledPresenceRadio value="always">
                          <StyledPresenceCopy>
                            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                              Always visible
                            </MDSText>
                            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                              Always displays in the corner of your screen
                            </MDSText>
                          </StyledPresenceCopy>
                        </StyledPresenceRadio>
                        <StyledPresenceRadio value="unread">
                          <StyledPresenceCopy>
                            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                              When unread
                            </MDSText>
                            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                              Displays when Mem Agent has an unread message
                            </MDSText>
                          </StyledPresenceCopy>
                        </StyledPresenceRadio>
                        <StyledPresenceRadio value="on_demand">
                          <StyledPresenceCopy>
                            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                              On demand
                            </MDSText>
                            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                              Displays when summoned with the keyboard
                            </MDSText>
                          </StyledPresenceCopy>
                        </StyledPresenceRadio>
                      </StyledPresenceRadioGroup>
                    </StyledFloatingAgentSettingsCard>
                  </StyledFloatingAgentSection>

                  <StyledPreferenceArea>
                    <StyledSectionHeader
                      variant={MDSTextVariant.TextXs}
                      weight={MDSTextWeight.Smedium}
                      color={MDSTextColor.Secondary}
                    >
                      PUSH-TO-REMEMBER
                    </StyledSectionHeader>
                    <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
                      Immediately start speaking to Mem Agent with a keypress, even when hidden.
                    </MDSText>
                    <StyledPreferenceRow>
                      <StyledPreferencePrimary>
                        <StyledPreferenceToggleFrame>
                          <MDSToggle
                            aria-label="Enable Push-to-Remember shortcut"
                            checked={isHoldToTalkShortcutEnabled}
                            disabled={isHoldToTalkControlDisabled}
                            onChange={event =>
                              onHoldToTalkEnabledChange(event.currentTarget.checked)
                            }
                          />
                        </StyledPreferenceToggleFrame>
                        <StyledPreferenceCopy>
                          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                            Use a shortcut to summon Mem Agent
                          </MDSText>
                        </StyledPreferenceCopy>
                      </StyledPreferencePrimary>
                      <StyledPreferenceControls>
                        <MemAgentShortcutDropdown
                          value={resolvedHoldToTalkKey}
                          isDisabled={isHoldToTalkControlDisabled || !isHoldToTalkShortcutEnabled}
                          isSaving={isSavingHoldToTalkKey}
                          onChange={onHoldToTalkKeyChange}
                        />
                      </StyledPreferenceControls>
                    </StyledPreferenceRow>
                  </StyledPreferenceArea>
                </>
              ) : null}

              <StyledPreferenceArea aria-label="Mem Agent sounds">
                <StyledSectionHeader
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.Smedium}
                  color={MDSTextColor.Secondary}
                >
                  SOUNDS
                </StyledSectionHeader>
                <StyledPreferenceRow>
                  <StyledPreferencePrimary>
                    <StyledPreferenceToggleFrame>
                      <MDSToggle
                        aria-label="Play sound when new messages arrive"
                        checked={newMessageSoundEnabled}
                        onChange={event =>
                          onNewMessageSoundEnabledChange(event.currentTarget.checked)
                        }
                      />
                    </StyledPreferenceToggleFrame>
                    <StyledPreferenceCopy>
                      <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                        Play sound when new messages arrive
                      </MDSText>
                    </StyledPreferenceCopy>
                  </StyledPreferencePrimary>
                </StyledPreferenceRow>
              </StyledPreferenceArea>
            </StyledContentStack>
          ) : (
            <StyledContentStack $pageKind="communication-channels">
              <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
                Enable communication with Mem Agent on iMessage, WhatsApp, Slack, and Teams (coming
                soon). Mem will send you reminders on the channels they were requested.
              </MDSText>

              {showConnectedChannels ? (
                <StyledConnectorSection aria-label="Connected channels">
                  <StyledSectionHeader
                    variant={MDSTextVariant.TextXs}
                    weight={MDSTextWeight.Smedium}
                    color={MDSTextColor.Secondary}
                  >
                    CONNECTED CHANNELS
                  </StyledSectionHeader>
                  <StyledConnectorList>
                    {showEmailSenderAllowlist ? (
                      <SettingsEmailSenderAllowlistSection
                        emailChannelAddress={emailChannelAddress}
                        protectedEmailSenderAllowlistEntry={protectedEmailSenderAllowlistEntry}
                        emailSenderAllowlist={emailSenderAllowlist}
                        isSavingEmailSenderAllowlist={isSavingEmailSenderAllowlist}
                        canSaveEmailSenderAllowlist={canSaveEmailSenderAllowlist}
                        onSaveEmailSenderAllowlist={onSaveEmailSenderAllowlist}
                      />
                    ) : null}
                    {connectedChannelRows.map(row => (
                      <Fragment key={row.option}>{row.element}</Fragment>
                    ))}
                  </StyledConnectorList>
                </StyledConnectorSection>
              ) : null}

              {availableChannelRows.length > 0 ? (
                <StyledConnectorSection aria-label="Available channels">
                  <StyledSectionHeader
                    variant={MDSTextVariant.TextXs}
                    weight={MDSTextWeight.Smedium}
                    color={MDSTextColor.Secondary}
                  >
                    AVAILABLE CHANNELS
                  </StyledSectionHeader>
                  <StyledConnectorList>
                    {availableChannelRows.map(row => (
                      <Fragment key={row.option}>{row.element}</Fragment>
                    ))}
                  </StyledConnectorList>
                </StyledConnectorSection>
              ) : null}
            </StyledContentStack>
          )}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
};

const StyledContentStack = styled.div<{ $pageKind: SettingsMemAgentPageKind }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $pageKind }) => ($pageKind === "general" ? theme.spacing[0] : theme.spacing[8])};
  padding-bottom: ${({ theme }) => theme.spacing[8]};
  width: 100%;
  max-width: 700px;
  container-type: inline-size;
  container-name: reminders-settings-content;
`;

const StyledIntroPanel = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 288px) minmax(0, 1fr);
  align-items: center;
  gap: ${({ theme }) => theme.spacing[6]};
  box-sizing: border-box;
  min-height: 212px;
  padding: ${({ theme }) => theme.spacing[5]};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.background.card.default};

  @container mds-panel (max-width: 750px) {
    grid-template-columns: 1fr;
  }

  @container reminders-settings-content (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StyledIntroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledIntroActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding-top: ${({ theme }) => theme.spacing[2]};

  @container reminders-settings-content (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const StyledIntroMediaFrame = styled.div<{ $isWindows: boolean }>`
  align-self: center;
  width: 100%;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.modal};
  aspect-ratio: ${({ $isWindows }) => ($isWindows ? "476 / 267" : "756 / 334")};
`;

const StyledIntroMediaImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledSectionHeader = styled(MDSText)`
  letter-spacing: 0.02em;
`;

const StyledFloatingAgentSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  padding-top: ${({ theme }) => theme.spacing[8]};
`;

const StyledFloatingAgentSettingsCard = styled.div`
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surface.pane};
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const StyledPresenceRadioGroup = styled(MDSRadioGroup)`
  gap: 0;
`;

const StyledPresenceRadio = styled(MDSRadio)`
  position: relative;
  box-sizing: border-box;
  min-height: 72px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[3]};

  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing[6]};
  }

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: -12px;
    left: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.border.default};
  }

  & > label {
    flex: 1;
  }
`;

const StyledPresenceCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledPreferenceArea = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  padding-top: ${({ theme }) => theme.spacing[5]};
`;

const StyledPreferenceRow = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  box-sizing: border-box;
  min-height: 80px;
  padding: ${({ theme }) =>
    `${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[3]}`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surface.pane};

  @container reminders-settings-content (max-width: 520px) {
    align-items: flex-start;
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const StyledPreferencePrimary = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  min-width: 0;
`;

const StyledPreferenceToggleFrame = styled.div`
  display: flex;
  align-items: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
`;

const StyledPreferenceCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledPreferenceControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  flex: 0 0 auto;
`;

const StyledConnectorSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const StyledConnectorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledConnectorRow = styled.section`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 80px;
  padding: 0 ${({ theme }) => theme.spacing[5]} 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surface.pane};
`;

const StyledConnectorIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  height: 16px;
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const StyledConnectorTitle = styled(MDSText)`
  min-width: 0;
`;

const StyledConnectorCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledConnectorActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex: 0 0 auto;
  margin-left: ${({ theme }) => theme.spacing[4]};
`;

const StyledConnectorButton = styled(MDSButton)`
  flex: 0 0 auto;
  min-width: 77px;
`;

const StyledConnectedStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StyledSubscriptionNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledSubscriptionNoticeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const StyledSubscriptionNoticeLink = styled(MDSLink)`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration-style: dotted;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: 25%;
  text-underline-position: from-font;
`;
